import React, { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { getAdminMetersList, getRoutes, updateMeterInRoute } from "../services/api";
import { cloudinaryThumb } from "../services/cloudinary";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import * as XLSX from "xlsx";
import { X, Download, Search, Pencil } from "lucide-react";

export default function MedicionesPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === 'admin-plus';

  // Filtros
  const [search, setSearch]           = useState("");
  const [filterRoute, setFilterRoute] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(100);
  const [customSize, setCustomSize]   = useState("");
  const [showCustom, setShowCustom]   = useState(false);
  const customInputRef = useRef(null);

  // Datos del servidor
  const [data, setData]           = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [loading, setLoading]     = useState(true);
  const [routesList, setRoutesList] = useState([]);

  // Stats (totales globales sin filtro de página)
  const [stats, setStats] = useState({ total: 0, checked: 0, pending: 0, incidents: 0 });

  const [lightbox, setLightbox] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  // Cargar lista de rutas para el selector
  useEffect(() => {
    getRoutes()
      .then((routes) => setRoutesList(routes.map((r) => ({ id: r.id, name: r.name }))))
      .catch(() => {});
  }, []);

  // Cargar medidores paginados
  const load = useCallback((page, size, s, route, status) => {
    setLoading(true);
    getAdminMetersList({ page, limit: size, search: s || undefined, routeId: route || undefined, status: status || undefined })
      .then((res) => {
        setData(res.data);
        setPagination(res.pagination);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  // Cargar stats globales una sola vez
  useEffect(() => {
    Promise.all([
      getAdminMetersList({ page: 1, limit: 1 }),
      getAdminMetersList({ page: 1, limit: 1, status: "checked" }),
      getAdminMetersList({ page: 1, limit: 1, status: "pending" }),
      getAdminMetersList({ page: 1, limit: 1, status: "incident" }),
    ]).then(([all, checked, pending, incidents]) => {
      setStats({
        total:     all.pagination.total,
        checked:   checked.pagination.total,
        pending:   pending.pagination.total,
        incidents: incidents.pagination.total,
      });
    }).catch(() => {});
  }, []);

  // Recargar cuando cambian filtros o página
  useEffect(() => {
    load(currentPage, pageSize, search, filterRoute, filterStatus);
  }, [currentPage, pageSize, filterRoute, filterStatus, load]);

  // Búsqueda con debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
      load(1, pageSize, search, filterRoute, filterStatus);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  function handlePageSize(val) {
    if (val === "custom") {
      setShowCustom(true);
      setTimeout(() => customInputRef.current?.focus(), 50);
    } else {
      setPageSize(val);
      setShowCustom(false);
      setCustomSize("");
      setCurrentPage(1);
    }
  }

  function applyCustomSize() {
    const n = parseInt(customSize);
    if (n > 0) { setPageSize(n); setCurrentPage(1); setShowCustom(false); }
  }

  function handleFilterChange(setter) {
    return (e) => { setter(e.target.value); setCurrentPage(1); };
  }

  const totalPages = pagination.pages;
  const paginated  = data;

  function exportExcel() {
    const rows = data.map((m) => ({
      "Código medidor": m.meter_code || "",
      Ruta: m.route_name,
      Latitud: m.latitude,
      Longitud: m.longitude,
      Estado: m.has_incident ? "Incidencia" : m.checked ? "Medido" : "Pendiente",
      "Valor medición": m.reading_value || "",
      Auditor: m.reader_name || "",
      "Fecha medición": m.read_at
        ? format(new Date(m.read_at), "dd/MM/yyyy HH:mm", { locale: es })
        : "",
      "Foto URL": m.photo || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Auditorias");
    XLSX.writeFile(wb, `auditorias_${format(new Date(), "yyyyMMdd")}.xlsx`);
  }

  return (
    <div>
      {/* Header */}
      <div style={s.header}>
        <h1 style={s.title}>
          Auditorias <span style={s.badge}>{stats.total}</span>
        </h1>
        {isAdminPlus && (
          <button style={s.btnExport} onClick={exportExcel}>
            <Download size={14} style={{ marginRight: 6 }} />
            Exportar Excel
          </button>
        )}
      </div>

      {/* Stats */}
      <div style={s.statsRow}>
        <StatCard label="Total medidores" value={stats.total}     color="#D4620D" />
        <StatCard label="Medidos"         value={stats.checked}   color="#16a34a" />
        <StatCard label="Pendientes"      value={stats.pending}   color="#6b6b66" />
        <StatCard label="Con incidencia"  value={stats.incidents} color="#f59e0b" />
      </div>

      {/* Filters */}
      <div style={s.filterBar}>
        <div style={s.searchWrap}>
          <Search size={14} style={{ color: "var(--text-2)", flexShrink: 0 }} />
          <input
            style={s.searchInput}
            placeholder="Buscar código de medidor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button style={s.clearBtn} onClick={() => setSearch("")}>
              <X size={13} />
            </button>
          )}
        </div>

        <select
          style={s.filterSelect}
          value={filterRoute}
          onChange={handleFilterChange(setFilterRoute)}
        >
          <option value="">Todas las rutas</option>
          {routesList.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        <select
          style={s.filterSelect}
          value={filterStatus}
          onChange={handleFilterChange(setFilterStatus)}
        >
          <option value="">Todos los estados</option>
          <option value="checked">Medidos</option>
          <option value="pending">Pendientes</option>
          <option value="incident">Con incidencia</option>
        </select>

        <span style={s.resultCount}>
          {pagination.total} resultado{pagination.total !== 1 ? "s" : ""}
        </span>

        {/* Selector de items por página */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
          <span style={{ fontSize: 12, color: "var(--text-2)", fontWeight: 600, whiteSpace: "nowrap" }}>Por página:</span>
          {[100, 200, 300].map((n) => (
            <button
              key={n}
              style={{ ...s.pageSizeBtn, ...(pageSize === n && !showCustom ? s.pageSizeBtnActive : {}) }}
              onClick={() => handlePageSize(n)}
            >
              {n}
            </button>
          ))}
          {showCustom ? (
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <input
                ref={customInputRef}
                style={s.customSizeInput}
                type="number"
                min="1"
                placeholder="Ej: 500"
                value={customSize}
                onChange={(e) => setCustomSize(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyCustomSize()}
              />
              <button style={s.pageSizeBtnActive} onClick={applyCustomSize}>OK</button>
            </div>
          ) : (
            <button
              style={{ ...s.pageSizeBtn, ...(![100, 200, 300].includes(pageSize) ? s.pageSizeBtnActive : {}) }}
              onClick={() => handlePageSize("custom")}
            >
              {![100, 200, 300].includes(pageSize) ? pageSize : "Otro"}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={s.loading}>Cargando...</div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                {[
                  "Foto medición",
                  "Código medidor",
                  "Ruta",
                  "Ubicación",
                  "Auditor",
                  "Fecha medición",
                  "Valor",
                  "Estado",
                  ...(isAdminPlus ? [""] : []),
                ].map((h, i) => (
                  <th key={i} style={s.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td
                    colSpan={isAdminPlus ? 9 : 8}
                    style={{
                      ...s.td,
                      textAlign: "center",
                      color: "var(--text-2)",
                      padding: 48,
                    }}
                  >
                    Sin resultados
                  </td>
                </tr>
              ) : (
                paginated.map((m) => (
                  <tr key={m.id} style={s.tr}>
                    {/* Photo */}
                    <td style={s.td}>
                      {m.photo ? (
                        <img
                          src={cloudinaryThumb(m.photo, 60, 60)}
                          alt=""
                          style={s.thumb}
                          loading="lazy"
                          onClick={() => setLightbox(m.photo)}
                        />
                      ) : (
                        <div style={s.noPhoto}>—</div>
                      )}
                    </td>

                    {/* Meter code */}
                    <td style={s.td}>
                      <span style={s.code}>{m.meter_code || "—"}</span>
                    </td>

                    {/* Route */}
                    <td style={s.td}>
                      <span style={s.routeChip}>{m.route_name}</span>
                    </td>

                    {/* Location */}
                    <td style={s.td}>
                      {m.latitude && m.longitude ? (
                        <a
                          href={`https://www.google.com/maps?q=${parseFloat(m.latitude).toFixed(6)},${parseFloat(m.longitude).toFixed(6)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={s.mapPinLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335"/>
                            <circle cx="12" cy="9" r="2.8" fill="white"/>
                          </svg>
                        </a>
                      ) : <span>—</span>}
                    </td>

                    {/* Reader */}
                    <td style={s.td}>
                      <span style={s.reader}>{m.reader_name || "—"}</span>
                    </td>

                    {/* Date */}
                    <td style={s.td}>
                      <span style={s.date}>
                        {m.read_at
                          ? format(new Date(m.read_at), "dd/MM/yy HH:mm", {
                              locale: es,
                            })
                          : "—"}
                      </span>
                    </td>

                    {/* Value */}
                    <td style={s.td}>
                      <span style={m.reading_value ? s.value : s.valueMissing}>
                        {m.reading_value || "—"}
                      </span>
                    </td>

                    {/* Status */}
                    <td style={s.td}>
                      <StatusBadge meter={m} />
                    </td>

                    {/* Edit — solo admin-plus */}
                    {isAdminPlus && (
                      <td style={s.td}>
                        <button
                          style={s.editBtn}
                          onClick={(e) => { e.stopPropagation(); setEditTarget(m); }}
                          title="Editar auditoría"
                        >
                          <Pencil size={13} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div style={s.pagination}>
          <button style={s.pageBtn} disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>«</button>
          <button style={s.pageBtn} disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>‹ Ant</button>
          <span style={s.pageInfo}>
            Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
            <span style={{ color: "var(--text-3)", marginLeft: 8, fontSize: 11 }}>
              ({((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, pagination.total)} de {pagination.total})
            </span>
          </span>
          <button style={s.pageBtn} disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Sig ›</button>
          <button style={s.pageBtn} disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>»</button>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div style={s.lightboxOverlay} onClick={() => setLightbox(null)}>
          <button style={s.lightboxClose} onClick={() => setLightbox(null)}>
            <X size={22} />
          </button>
          <img
            src={lightbox}
            alt=""
            style={s.lightboxImg}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Modal editar auditoría */}
      {editTarget && (
        <EditMeterModal
          meter={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            setData((prev) => prev.map((m) =>
              m.id === updated.id
                ? { ...m, meter_code: updated.meter_code, reading_value: updated.reading_value, checked: updated.checked }
                : m,
            ));
            setEditTarget(null);
          }}
        />
      )}
    </div>
  );
}

function EditMeterModal({ meter, onClose, onSaved }) {
  const [form, setForm] = useState({
    meter_code: meter.meter_code || "",
    reading_value: meter.reading_value || "",
    checked: meter.checked,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      await updateMeterInRoute(meter.route_id, meter.id, {
        meter_code: form.meter_code || null,
        reading_value: form.reading_value || null,
        checked: form.checked,
      });
      onSaved({ ...meter, ...form });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={em.overlay} onClick={onClose}>
      <div style={em.box} onClick={(e) => e.stopPropagation()}>
        <div style={em.header}>
          <h2 style={em.title}>Editar auditoría</h2>
          <button style={em.closeBtn} onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={em.label}>Código de medidor</label>
          <input
            style={em.input}
            value={form.meter_code}
            onChange={(e) => setForm((f) => ({ ...f, meter_code: e.target.value }))}
            placeholder="Sin código"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={em.label}>Valor de medición</label>
          <input
            style={em.input}
            value={form.reading_value}
            onChange={(e) => setForm((f) => ({ ...f, reading_value: e.target.value }))}
            placeholder="Sin valor"
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={em.label}>Estado</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { val: true,  label: "✓ Medido",    bg: "var(--success-bg)", color: "var(--success-text)" },
              { val: false, label: "○ Pendiente",  bg: "var(--bg-subtle)",  color: "var(--text-2)" },
            ].map(({ val, label, bg, color }) => (
              <button
                key={String(val)}
                style={{
                  padding: "8px 16px", borderRadius: 10, border: "2px solid",
                  borderColor: form.checked === val ? color : "var(--border)",
                  background: form.checked === val ? bg : "var(--bg-card)",
                  color: form.checked === val ? color : "#9a9a93",
                  fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Montserrat, sans-serif",
                }}
                onClick={() => setForm((f) => ({ ...f, checked: val }))}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && <p style={{ color: "var(--error-text)", fontSize: 13, marginBottom: 12 }}>{error}</p>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button style={em.btnCancel} onClick={onClose}>Cancelar</button>
          <button style={em.btnSave} onClick={handleSave} disabled={saving}>
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

const em = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  box:     { background: "var(--bg-card)", borderRadius: 14, padding: 28, width: "92%", maxWidth: 440 },
  header:  { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 },
  title:   { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  closeBtn:{ background: "none", border: "none", cursor: "pointer", color: "var(--text-2)", display: "flex", alignItems: "center" },
  label:   { display: "block", fontSize: 13, fontWeight: 700, color: "var(--text-2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" },
  input:   { width: "100%", padding: "10px 12px", borderRadius: 10, 
 fontSize: 13, outline: "none", fontFamily: "Montserrat, sans-serif", boxSizing: "border-box" },
  btnCancel: { padding: "9px 18px", borderRadius: 12, 
 background: "var(--bg-card)", color: "var(--text-2)", fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "Montserrat, sans-serif" },
  btnSave:   { padding: "9px 18px", borderRadius: 12, border: "none", background: "#D4620D", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "Montserrat, sans-serif" },
};

function StatCard({ label, value, color }) {
  return (
    <div style={s.statCard}>
      <span style={{ ...s.statValue, color }}>{value}</span>
      <span style={s.statLabel}>{label}</span>
    </div>
  );
}

function StatusBadge({ meter }) {
  if (meter.has_incident)
    return (
      <span
        style={{
          ...s.statusBadge,
          background: "var(--warning-bg)",
          color: "var(--warning-text)",
        }}
      >
        ⚠ Incidencia
      </span>
    );
  if (meter.checked)
    return (
      <span
        style={{
          ...s.statusBadge,
          background: "var(--success-bg)",
          color: "var(--success-text)",
        }}
      >
        ✓ Medido
      </span>
    );
  return (
    <span
      style={{
        ...s.statusBadge,
        background: "var(--bg-subtle)",
        color: "var(--text-2)",
      }}
    >
      ○ Pendiente
    </span>
  );
}

const s = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  title: { fontSize: 24, fontWeight: 700, color: "var(--text)", margin: 0 },
  badge: {
    background: "var(--primary-bg)",
    color: "#D4620D",
    borderRadius: 6,
    padding: "2px 10px",fontSize: 14,
    marginLeft: 8,
  },
  btnExport: {
    display: "flex",
    alignItems: "center",
    padding: "8px 14px",
    borderRadius: 10,
    border: "none",
    background: "#16a34a",
    cursor: "pointer",fontSize: 13,
    fontWeight: 700,
    color: "#fff",
    fontFamily: "Montserrat, sans-serif",
  },

  statsRow: {
    display: "flex",
    gap: 12,
    marginBottom: 20,
  },
  statCard: {
    flex: 1,

borderRadius: 12,
    padding: "16px 20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: -0.5,
  },
  statLabel: {
    fontSize: 13,
    color: "var(--text-2)",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },

  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,

padding: "12px 16px",
    borderRadius: 12,
    marginBottom: 16,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    flexWrap: "wrap",
  },
  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 200,
    background: "#f8fafc",
    borderRadius: 10,
    padding: "7px 10px",

},
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",fontSize: 13,
    color: "var(--text)",
    background: "transparent",
    fontFamily: "Montserrat, sans-serif",
  },
  clearBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--text-2)",
    display: "flex",
    alignItems: "center",
    padding: 0,
  },
  filterSelect: {
    padding: "7px 10px",
    borderRadius: 10,    

fontSize: 13,
    color: "var(--text)",
    outline: "none",
    fontFamily: "Montserrat, sans-serif",

},
  resultCount: {
    marginLeft: "auto",fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    whiteSpace: "nowrap",
  },

  loading: { textAlign: "center", padding: 60, color: "var(--text-2)", fontSize: 14 },

  tableWrap: {
    borderRadius: 14,
    overflow: "auto",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "12px 16px",
    textAlign: "left",fontSize: 13,
    color: "var(--text-2)",
    fontWeight: 700,
    borderBottom: "1px solid var(--border)",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  tr: { borderBottom: "1px solid var(--border)" },
  td: {
    padding: "10px 16px",
    verticalAlign: "middle",
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 700,fontSize: 13,
    color: "var(--text-2)",
  },

  thumb: {
    width: 48,
    height: 48,
    borderRadius: 10,
    objectFit: "cover",
    display: "block",
    background: "var(--bg-subtle)",
    cursor: "zoom-in",
  },
  noPhoto: { color: "var(--text-3)", fontSize: 18, paddingLeft: 8 },

  code: { fontSize: 13, fontWeight: 700, color: "var(--text-2)" },
  routeChip: {
    display: "inline-block",fontSize: 13,
    fontWeight: 700,
    color: "#D4620D",
    background: "var(--primary-bg)",
    borderRadius: 6,
    padding: "2px 8px",
  },
  mapPinLink: { color: "var(--error-text)", display: "inline-flex", alignItems: "center" },
  reader: { fontSize: 13, fontWeight: 700, color: "var(--text-2)" },
  date: { fontSize: 13, fontWeight: 700, color: "var(--text-2)", whiteSpace: "nowrap" },
  value: { fontSize: 13, fontWeight: 700, color: "var(--text-2)" },
  valueMissing: { fontSize: 13, fontWeight: 700, color: "var(--text-2)" },
  editBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 30, height: 30, borderRadius: 7, 

color: "var(--text-2)", cursor: "pointer",
  },

  statusBadge: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 6,fontSize: 13,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },

  pageSizeBtn: {
    padding: "4px 10px", borderRadius: 7, border: "1px solid var(--border)",
    background: "var(--bg-card)", color: "var(--text-2)", fontSize: 12,
    fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  pageSizeBtnActive: {
    padding: "4px 10px", borderRadius: 7, border: "1px solid #D4620D",
    background: "#FBF0E8", color: "#D4620D", fontSize: 12,
    fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  customSizeInput: {
    width: 80, padding: "4px 8px", borderRadius: 7, border: "1px solid var(--border)",
    fontSize: 12, fontFamily: "Montserrat, sans-serif", color: "var(--text)", outline: "none",
  },
  pagination: {
    display: "flex", alignItems: "center", justifyContent: "center",
    gap: 8, marginTop: 20, flexWrap: "wrap",
  },
  pageBtn: {
    padding: "7px 14px", borderRadius: 9, border: "1px solid var(--border)",
    background: "var(--bg-card)", color: "var(--text-2)", fontSize: 13,
    fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  pageInfo: {
    fontSize: 13, color: "var(--text-2)", fontWeight: 600,
    padding: "0 8px",
  },

  lightboxOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.92)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3000,
    cursor: "zoom-out",
  },
  lightboxImg: {
    maxWidth: "92vw",
    maxHeight: "92vh",
    objectFit: "contain",
    borderRadius: 10,
    boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
  },
  lightboxClose: {
    position: "absolute",
    top: 20,
    right: 20,
    background: "rgba(255,255,255,0.15)",
    border: "none",
    borderRadius: "50%",
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#fff",
  },
};
